import { useStudyText } from '@/i18n/studyText';
import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { SupervisedIcons } from './components/StudentIcons';
import { StudentCabinetGrid } from './components/StudentCabinetGrid';
import { ScientificWorkOverlay } from './components/ScientificWorkOverlay';
import { GatewayCreateRequestOverlay } from './components/GatewayCreateRequestOverlay';
import { MyRequestsOverlay } from './components/MyRequestsOverlay';
import { LiteratureSearchOverlay } from './components/LiteratureSearchOverlay';
import { SharedProjectsOverlay } from './components/SharedProjectsOverlay';
import { OpportunitiesOverlay } from './components/OpportunitiesOverlay';
import { EnuSourcesOverlay } from './components/EnuSourcesOverlay';
import { SuperAiModal, SubmitTaskSolutionModal } from './components/StudentModals';
import { SupervisedStudentCabinetCard, StudentTaskItem, WorkspaceSummary } from './types';
import { supervisedStudentApi } from '@/features/workspace/api';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { MeetingsAndDutiesOverlay } from './components/MeetingsAndDutiesOverlay';
import { useAuth } from '@/hooks/useAuth';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

const CARD_PATHS: Record<SupervisedStudentCabinetCard, string> = {
  SCIENTIFIC_WORK: 'scientific-work',
  MY_REQUESTS: 'requests',
  ACADEMIC_CONNECTIONS: 'connections',
  FIND_LITERATURE: 'literature',
  PROGRAMS_AND_TOOLS: 'tools',
  RESEARCH_PROJECTS: 'scientific-projects',
  OPPORTUNITIES: 'opportunities',
  MEETINGS_AND_DUTIES: 'meetings',
  FILES_AND_DOCUMENTS: 'files',
  ENU_SOURCES: 'enu-sources',
};

export const SupervisedStudentPage: React.FC = () => {
  const st = useStudyText();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwnerPreview = Boolean(user?.roles?.includes('SYSTEM_OWNER'));
  const backToCabinet = () => navigate(routes.supervisedStudent);
  const openCard = (card: SupervisedStudentCabinetCard) => {
    const segment = CARD_PATHS[card];
    if (segment) navigate(segment);
  };
  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedTaskToSubmit, setSelectedTaskToSubmit] = useState<StudentTaskItem | null>(null);

  const [studentNotes, setStudentNotes] = useState('');

  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    loadWorkspace();
  }, []);

  const loadWorkspace = async () => {
    setLoading(true);
    try {
      const data = await supervisedStudentApi.workspace();
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const profile = summary?.profile;
  const kpiNumber = (id: string, fallback: number) => {
    if (!Array.isArray(summary?.kpis)) return fallback;
    const found = summary.kpis.find((item: { id?: string; value?: string | number }) => item.id === id);
    if (typeof found?.value === 'number') return found.value;
    if (typeof found?.value === 'string') {
      const parsed = Number(found.value.replace('%', ''));
      if (!Number.isNaN(parsed)) return parsed;
    }
    return fallback;
  };
  const liveKpis = Array.isArray(summary?.kpis);
  const kpis: {
    progress_percentage: number;
    current_stage: string;
    pending_tasks_count: number;
    active_requests_count: number;
    upcoming_meetings_count: number;
    duties_count: number;
    literature_count: number;
    tools_count: number;
    files_count: number;
  } = liveKpis
    ? {
        progress_percentage: kpiNumber('progress', profile?.progress_pct ?? 0),
        current_stage: profile?.study_year_label || profile?.rating_rank || st('Научная работа'),
        pending_tasks_count: kpiNumber('tasks', 0),
        active_requests_count: kpiNumber('requests', 0),
        upcoming_meetings_count: kpiNumber('meetings', 0),
        duties_count: 0,
        literature_count: 0,
        tools_count: 36,
        files_count: 0,
      }
    : (summary?.kpis && !Array.isArray(summary.kpis))
      ? summary.kpis
      : {
          progress_percentage: 0,
          current_stage: st('Темы пока не назначены научным руководителем'),
          pending_tasks_count: 0,
          active_requests_count: 0,
          upcoming_meetings_count: 0,
          duties_count: 0,
          literature_count: 0,
          tools_count: 0,
          files_count: 0,
        };
  const studentCode = profile?.academia_id || profile?.student_id || user?.academiaId || '—';
  const studentName = profile?.full_name?.trim() || user?.fullName?.trim() || user?.email || '—';
  const programTitle = profile?.program_title || profile?.specialty || '';
  const programLine = profile?.program_code ? `${profile.program_code} · ${programTitle}` : programTitle;
  const departmentLine = profile?.department || profile?.study_year_label || '';
  const supervisorName = profile?.supervisor_name || profile?.supervisor?.name || st('Не назначен');
  const supervisorTitle = profile?.supervisor_title || profile?.supervisor?.degree || 'Научное руководство';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col role-cabinet-page">
      <Routes>
      <Route
        index
        element={
      <CabinetWorkspaceFrame
        links={{
          notebook: `${routes.supervisedStudent}/literature`,
          calendar: `${routes.supervisedStudent}/meetings`,
          meetings: `${routes.supervisedStudent}/meetings`,
          connections: `${routes.supervisedStudent}/connections`,
          alliances: `${routes.supervisedStudent}/scientific-projects`,
          qa: routes.portalSection('instructions'),
          support: routes.portalSection('contacts'),
        }}
      >
      <div className="px-0 py-5 space-y-5">
        <div className="role-hero" style={{ ['--role-accent' as string]: '#e2ad3a' }}>
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-tr from-amber-500 to-orange-400 flex items-center justify-center text-slate-950 font-black text-lg shrink-0">
              <SupervisedIcons.Graduation className="w-7 h-7" />
            </div>
            <div className="min-w-0">
              <small>
                {studentCode} · Обучающийся под руководством
              </small>
              <h2 className="truncate">{studentName}</h2>
              <p>
                {programLine} · {departmentLine}
              </p>
            </div>
          </div>
          <div className="role-hero-actions">
            <button type="button" className="role-btn" onClick={() => setAiModalOpen(true)}>
              Super AI
            </button>
            <button type="button" className="role-btn primary" onClick={() => setGatewayOpen(true)}>
              + Создать заявку
            </button>
          </div>
        </div>

        <div className="role-statgrid" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#e2ad3a' }}
            onClick={() => openCard('SCIENTIFIC_WORK')}
          >
            <strong>{kpis.progress_percentage}%</strong>
            <span>Прогресс НИР</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#41d2ff' }}
            onClick={() => openCard('SCIENTIFIC_WORK')}
          >
            <strong className="!text-sm">{kpis.current_stage}</strong>
            <span>Текущий этап</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#5da8ff' }}
            onClick={() => openCard('SCIENTIFIC_WORK')}
          >
            <strong>{kpis.pending_tasks_count}</strong>
            <span>Задачи в работе</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#49ddb7' }}
            onClick={() => openCard('MEETINGS_AND_DUTIES')}
          >
            <strong>{kpis.upcoming_meetings_count}</strong>
            <span>Встречи</span>
          </button>
          <button
            type="button"
            className="role-stat"
            style={{ ['--a' as string]: '#9e80ff' }}
            onClick={() => openCard('MY_REQUESTS')}
          >
            <strong>{kpis.active_requests_count}</strong>
            <span>Заявки</span>
          </button>
        </div>
      </div>

      <div className="flex-1 px-0 py-6 space-y-6">
        <div className="owner-hub-top-actions">
          <button type="button" className="owner-hub-ai-action" onClick={() => setAiModalOpen(true)}>
            <span className="spark" aria-hidden="true">
              ✦
            </span>
            <span>
              <strong>Super AI Академический консультант</strong>
              <small>Структура диплома, гипотезы и статистические критерии</small>
            </span>
            <b>Открыть →</b>
          </button>
          <button type="button" className="owner-hub-create" onClick={() => setGatewayOpen(true)}>
            + Создать заявку
          </button>
        </div>

        <StudentCabinetGrid onSelectCard={(card) => openCard(card)} />

        <ScientificGpsBlock openTo={`${routes.supervisedStudent}/scientific-work`} />

        <div className="supervised-secondary-band">
          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                <SupervisedIcons.Notebook className="w-4 h-4 text-amber-400" />
                Блокнот исследователя
              </h3>
              <span className="text-[10px] text-slate-500">Автосохранение</span>
            </div>
            <textarea
              rows={6}
              value={studentNotes}
              onChange={(e) => setStudentNotes(e.target.value)}
              placeholder="Заметки по эксперименту, вопросы научному руководителю, мысли..."
              className="w-full p-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-400 leading-relaxed resize-none"
            />
            <div className="pt-2 border-t border-slate-800/80 space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-[11px] font-bold text-teal-300 uppercase tracking-wider flex items-center gap-1.5">
                  <SupervisedIcons.Calendar className="w-3.5 h-3.5" />
                  Сентябрь 2026
                </h4>
                <span className="text-[10px] text-teal-400 font-semibold">10 сен — встреча</span>
              </div>
              <div className="text-[11px] text-slate-400 space-y-2">
                <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800/80 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-200">10 сен, 15:00</div>
                    <div className="text-slate-400 text-[10px]">Кабинет 412 (ЕНУ)</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-teal-500/20 text-teal-300">
                    Консультация
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800/80 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-200">12 сен, 09:00</div>
                    <div className="text-slate-400 text-[10px]">Ауд. 214 (Лаборатория)</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">
                    Дежурство
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                <SupervisedIcons.Tasks className="w-4 h-4 text-amber-400" />
                Срочные задачи
              </h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-400/20 text-amber-300">
                {summary?.urgent_tasks?.length || 2}
              </span>
            </div>

            <div className="space-y-2.5">
              {(summary?.urgent_tasks || []).map((task) => (
                <div
                  key={task.id}
                  className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-amber-400/40 transition flex flex-col justify-between space-y-2"
                >
                  <div>
                    <div className="text-xs font-bold text-slate-200">{task.title}</div>
                    <div className="text-[11px] text-amber-400/90 font-medium mt-1">
                      Дедлайн: {task.deadline}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedTaskToSubmit(task)}
                    className="w-full py-1.5 rounded-lg bg-amber-400/10 hover:bg-amber-400/20 text-amber-300 text-xs font-bold border border-amber-400/30 transition text-center"
                  >
                    Сдать решение &rarr;
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                  <SupervisedIcons.Connections className="w-4 h-4 text-blue-400" />
                  Научный руководитель
                </h3>
                <button
                  type="button"
                  onClick={() => openCard('ACADEMIC_CONNECTIONS')}
                  className="text-[10px] text-blue-400 hover:underline"
                >
                  Все контакты &rarr;
                </button>
              </div>

              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                <div className="font-bold text-xs text-slate-100">
                  {supervisorName}
                </div>
                <div className="text-[11px] text-slate-400">
                  {supervisorTitle}
                  {profile?.supervisor_department ? ` · ${profile.supervisor_department}` : ''}
                </div>
                <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Прием: Вт, Чт 14-17</span>
                  <button
                    type="button"
                    onClick={() => openCard('ACADEMIC_CONNECTIONS')}
                    className="px-2.5 py-1 rounded bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 font-semibold transition"
                  >
                    Чат
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                  <SupervisedIcons.Project className="w-4 h-4 text-cyan-400" />
                  Коллаборации и альянсы
                </h3>
              </div>
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 space-y-1.5">
                <div className="font-semibold text-cyan-300">Student Corpus Lab</div>
                <div className="text-[11px] text-slate-400">
                  Совместный проект кафедры теоретической лингвистики ЕНУ
                </div>
                <button
                  type="button"
                  onClick={() => openCard('RESEARCH_PROJECTS')}
                  className="w-full mt-2 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition text-center"
                >
                  Перейти в проект &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      </CabinetWorkspaceFrame>
        }
      />

      <Route
        path="scientific-work"
        element={
          <ScientificWorkOverlay
            isOpen
            onClose={backToCabinet}
            allowLevelSwitch={isOwnerPreview}
          />
        }
      />
      <Route
        path="requests"
        element={
          <MyRequestsOverlay
            isOpen
            onClose={backToCabinet}
            onOpenGateway={() => setGatewayOpen(true)}
            onOpenProjects={() => navigate(`${routes.supervisedStudent}/scientific-projects`)}
            onOpenOpportunities={() => navigate(`${routes.supervisedStudent}/opportunities`)}
          />
        }
      />
      <Route
        path="connections"
        element={
          <RoleAcademicConnectionsOverlay
            isOpen
            onClose={backToCabinet}
            roleContext="SUPERVISED_STUDENT"
            roleTitle="Обучающийся под руководством"
          />
        }
      />
      <Route
        path="literature"
        element={
          <LiteratureSearchOverlay
            isOpen
            onClose={backToCabinet}
            onLiteratureAdded={() => {
              loadWorkspace();
            }}
          />
        }
      />
      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="scientific-projects"
        element={<SharedProjectsOverlay isOpen onClose={backToCabinet} onOpenLiterature={() => navigate(`${routes.supervisedStudent}/literature`)} />}
      />
      <Route
        path="opportunities"
        element={
          <OpportunitiesOverlay
            isOpen
            onClose={backToCabinet}
            onOpenGateway={() => setGatewayOpen(true)}
          />
        }
      />
      <Route path="meetings" element={<MeetingsAndDutiesOverlay isOpen onClose={backToCabinet} />} />
      <Route path="meetings/*" element={<MeetingsAndDutiesOverlay isOpen onClose={backToCabinet} />} />
      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="enu-sources"
        element={<EnuSourcesOverlay isOpen onClose={backToCabinet} />}
      />
      </Routes>

      <GatewayCreateRequestOverlay
        isOpen={gatewayOpen}
        onClose={() => setGatewayOpen(false)}
        onSuccess={() => loadWorkspace()}
      />

      <SuperAiModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
      />

      <SubmitTaskSolutionModal
        isOpen={!!selectedTaskToSubmit}
        onClose={() => setSelectedTaskToSubmit(null)}
        task={selectedTaskToSubmit}
        onSuccess={() => loadWorkspace()}
      />
    </div>
  );
};

export default SupervisedStudentPage;
